import { useEffect, useMemo, useState } from "react";

import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  useLazyGetOrganizationByIdQuery,
  useLazyGetSectionsQuery,
  useLazyGetSurveyByIdQuery,
  useLazyGetSurveyStatisticsQuery,
} from "@/redux/service.js";
import { Roles } from "@/shared/utils/constants.tsx";
import SurveyViewDetails from "./components/SurveyViewDetails.jsx";
import BaseButton from "@/components_v2/Button/BaseButton.tsx";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer.tsx";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.tsx";
import AddDocumentIcon from "@/assets/icons/add-document.svg?react";
import {
  selectCurrentUser,
  selectIsAuthenticated,
} from "@/redux/authentication/selectors.ts";
import {
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants.ts";

export class InsufficientPermissions extends Error {
  constructor(message = "", navigateTo = null) {
    super(message);
    this.message = message;
    this.navigateTo = navigateTo;
  }
}

const SurveyOverview = () => {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const location = useLocation();

  const [getSurveyById, surveyResponse] = useLazyGetSurveyByIdQuery();
  const currentUser = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [getSections, sectionsResponse] = useLazyGetSectionsQuery();
  const [getSurveyStatics, surveyStatisticsResponse] =
    useLazyGetSurveyStatisticsQuery();
  const [getOrganizationById, organizationResponse] =
    useLazyGetOrganizationByIdQuery();
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  const getSurveyData = async () => {
    /**
     * Retrieves the survey data
     */
    let filters = { organization: null, sections: [], questions: null };
    if (!isAuthenticated) {
      throw new InsufficientPermissions(t("viewRequired"), "/login");
    }

    if (location.pathname.includes("public")) {
      navigate(`/reports/${surveyId}/report${location.search}`, {
        replace: true,
      });
    }

    if (currentUser) {
      if (searchParams.has("organization")) {
        filters.organization = searchParams.get("organization");
      } else {
        filters.organization = currentUser.organization.id;
      }
    }

    getSurveyById({
      surveyId,
      filters: { organization: filters.organization },
    });
    getSections({
      ids: filters.sections,
      survey: surveyId,
      parentOnly: true,
      pagination: "off",
    });
    setFilteredQuestions(filters.questions);
    getSurveyStatics({
      survey: surveyId,
      organization: filters.organization,
    });
    if (filters.organization) {
      getOrganizationById(filters.organization);
    }
  };

  useEffect(() => {
    getSurveyData().catch((err) => {
      if (err instanceof InsufficientPermissions) {
        notification.error({ message: err.message, key: err.message });
        navigate(err.navigateTo);
        return;
      }
      throw err;
    });
  }, [isAuthenticated]);

  const organizationName = useMemo(() => {
    if (organizationResponse.isSuccess) {
      return organizationResponse.data.name;
    }
    return null;
  }, [organizationResponse]);

  if (surveyResponse.isLoading || !currentUser) {
    return (
      <div className="flex justify-center full-width">
        <BaseSpinner />
      </div>
    );
  }

  if (surveyResponse.isError) {
    return <Navigate to="/" />;
  }

  return surveyResponse.isSuccess ? (
    <div className="flex flex-col gap-300">
      <div className="flex justify-between gap-100 flex-wrap">
        <h1 className="header-l-medium">
          {organizationName} - {surveyResponse.data.title}
        </h1>

        <div className="flex gap-100 flex-wrap">
          {currentUser &&
            organizationResponse.data &&
            currentUser.organization?.id === organizationResponse.data.id && (
              <>
                <BaseButton
                  variant={BUTTON_VARIANTS.TERTIARY}
                  type={BUTTON_TYPES.LINK}
                  to={`/reports/views/form`}
                  leadingIcon={
                    <BaseSvgRenderer
                      width="20"
                      height="20"
                      component={AddDocumentIcon}
                    />
                  }
                >
                  {t("buttons.generateViews")}
                </BaseButton>
              </>
            )}
        </div>
      </div>

      {sectionsResponse.isSuccess && (
        <SurveyViewDetails
          sections={sectionsResponse.data}
          surveyStatisticsResponse={surveyStatisticsResponse}
          filteredQuestions={filteredQuestions}
        />
      )}
    </div>
  ) : null;
};

export default SurveyOverview;
